import { Image, Spin } from "antd";
import { motion, useInView } from "framer-motion";
import styles from "./styles.module.scss";
import { Button, Card } from "../../custom";
import { Accordion } from "../../components";
import { useQueries } from "@tanstack/react-query";
import {
  getCampusExperienceByStudentLifeId,
  getCampusExperienceImageByCampusExperienceId,
  getCampusExperienceItemByCampusExperienceId,
  getFitnessAthleticsByStudentLifeId,
  getFitnessAthleticsImageByFitnessId,
  getFitnessAthleticsItemByFitnessId,
  getNewsAndEvents,
  getOverviewByStudentLifeId,
  getSchoolSummaryByStudentLifeId,
  getStudentActivityByStudentLifeId,
  getStudentActivityItemByStudentActivityId,
  getStudentLife,
  getSupportGuidanceByStudentLifeId,
  getSupportItemBySupportGuidanceId,
} from "./request";
import { sanitizeHtml } from "../../utils/sanitizeHtml";
import { useEffect, useRef, useState } from "react";
import { newsDates } from "../../utils/formatDate";
import { useLocation, useNavigate } from "react-router-dom";
import { routes } from "../../routes";
import MotionBox from "../../custom/motionBox";

const StudentLife = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const athleticRef = useRef(null);

  const isInView = useInView(athleticRef, { once: true });

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location]);

  const [studentLifeId, setStudentLifeId] = useState("");
  const [campusExperienceId, setCampusExperienceId] = useState("");
  const [supportGuidanceId, setSupportGuidanceId] = useState("");
  const [studentActivityId, setStudentActivityId] = useState("");
  const [fitnessAthleticsId, setFitnessAthleticsId] = useState("");

  const handleRegister = () => {
    navigate(routes.main.home);
  };

  const queries = useQueries({
    queries: [
      {
        queryKey: ["get-student-life"],
        queryFn: getStudentLife,
      },
      {
        queryKey: ["get-overview-studentLife-id"],
        queryFn: () => getOverviewByStudentLifeId(parseInt(studentLifeId)),
        enabled: !!parseInt(studentLifeId),
      },
      {
        queryKey: ["get-school-summary"],
        queryFn: () => getSchoolSummaryByStudentLifeId(parseInt(studentLifeId)),
        enabled: !!parseInt(supportGuidanceId),
      },
      {
        queryKey: ["get-campus-experience"],
        queryFn: () =>
          getCampusExperienceByStudentLifeId(parseInt(studentLifeId)),
        enabled: !!parseInt(studentLifeId),
      },
      {
        queryKey: ["get-campus-experience-id"],
        queryFn: () =>
          getCampusExperienceItemByCampusExperienceId(
            parseInt(campusExperienceId)
          ),
        enabled: !!parseInt(campusExperienceId),
      },
      {
        queryKey: ["get-supportGuidance-studentLife-id"],
        queryFn: () =>
          getSupportGuidanceByStudentLifeId(parseInt(studentLifeId)),
        enabled: !!parseInt(studentLifeId),
      },
      {
        queryKey: ["get-support-guidance-id"],
        queryFn: () =>
          getSupportItemBySupportGuidanceId(parseInt(supportGuidanceId)),
        enabled: !!parseInt(supportGuidanceId),
      },
      {
        queryKey: ["get-student-activity-id"],
        queryFn: () =>
          getStudentActivityByStudentLifeId(parseInt(studentLifeId)),
        enabled: !!parseInt(studentLifeId),
      },
      {
        queryKey: ["get-student-item-id"],
        queryFn: () =>
          getStudentActivityItemByStudentActivityId(
            parseInt(studentActivityId)
          ),
        enabled: !!parseInt(studentActivityId),
      },
      {
        queryKey: ["get-fitness-athletics-id"],
        queryFn: () =>
          getFitnessAthleticsByStudentLifeId(parseInt(studentLifeId)),
        enabled: !!parseInt(studentLifeId),
      },
      {
        queryKey: ["get-fitness-athletics-item-id"],
        queryFn: () =>
          getFitnessAthleticsItemByFitnessId(parseInt(fitnessAthleticsId)),
        enabled: !!parseInt(fitnessAthleticsId),
      },
    ],
  });

  const secondQuery = useQueries({
    queries: [
      {
        queryKey: ["get-fitness-athletics-image-id"],
        queryFn: () =>
          getFitnessAthleticsImageByFitnessId(parseInt(fitnessAthleticsId)),
        enabled: !!parseInt(fitnessAthleticsId),
      },
      {
        queryKey: ["get-campus-experience-image-id"],
        queryFn: () =>
          getCampusExperienceImageByCampusExperienceId(
            parseInt(campusExperienceId)
          ),
        enabled: !!parseInt(campusExperienceId),
      },
      {
        queryKey: ["get-all-news-event"],
        queryFn: getNewsAndEvents,
      },
    ],
  });

  const studentLifeQuery = queries[0];
  const overviewQuery = queries[1];
  const schoolSummaryQuery = queries[2];
  const campusExperienceQuery = queries[3];
  const campusExperienceItemQuery = queries[4];
  const supportGuidanceQuery = queries[5];
  const supportGuidanceItemQuery = queries[6];
  const studentActivityQuery = queries[7];
  const studentActivityItemQuery = queries[8];
  const fitnessAthleticsQuery = queries[9];
  const fitnessAthleticsItemQuery = queries[10];

  const fitnessAthleticsImageQuery = secondQuery[0];
  const campusExperienceImageQuery = secondQuery[1];
  const newsAndEventQuery = secondQuery[2];

  const isLoadingStudentLife = studentLifeQuery?.isLoading;

  const { data: newsAndEvent } = newsAndEventQuery;

  const newsAndEventData = newsAndEvent?.data ?? [];

  const studentLifeData =
    studentLifeQuery?.data?.data?.filter(
      (item) => item?.activeStatus === true
    ) ?? [];
  const overviewData =
    overviewQuery?.data?.data?.filter((item) => item?.activeStatus === true) ??
    [];
  const schoolSummaryData =
    schoolSummaryQuery?.data?.data?.filter(
      (item) => item?.activeStatus === true
    ) ?? [];
  const campusExperienceData =
    campusExperienceQuery?.data?.data?.filter(
      (item) => item?.activeStatus === true
    ) ?? [];
  const campusExperienceItemData =
    campusExperienceItemQuery?.data?.data?.filter(
      (item) => item?.activeStatus === true
    ) ?? [];
  const supportGuidanceData =
    supportGuidanceQuery?.data?.data?.filter(
      (item) => item?.activeStatus === true
    ) ?? [];
  const supportGuidanceItemData =
    supportGuidanceItemQuery?.data?.data?.filter(
      (item) => item?.activeStatus === true
    ) ?? [];
  const studentActivityData =
    studentActivityQuery?.data?.data?.filter(
      (item) => item?.activeStatus === true
    ) ?? [];
  const studentActivityItemData =
    studentActivityItemQuery?.data?.data?.filter(
      (item) => item?.activeStatus === true
    ) ?? [];
  const fitnessAthleticsData =
    fitnessAthleticsQuery?.data?.data?.filter(
      (item) => item?.activeStatus === true
    ) ?? [];
  const fitnessAthleticsItemData =
    fitnessAthleticsItemQuery?.data?.data?.filter(
      (item) => item?.activeStatus === true
    ) ?? [];
  const fitnessAthleticsImageData =
    fitnessAthleticsImageQuery?.data?.data?.filter(
      (item) => item?.activeStatus === true
    ) ?? [];
  const campusExperienceImageData =
    campusExperienceImageQuery?.data?.data?.filter(
      (item) => item?.activeStatus === true
    ) ?? [];

  const overviewToDisplay = overviewData[overviewData?.length - 1];

  const displayStudentActivity =
    campusExperienceData[campusExperienceData?.length - 1];

  const supportToDisplay = supportGuidanceData[supportGuidanceData?.length - 1];

  const studentActivityToDisplay =
    studentActivityData[studentActivityData?.length - 1];

  const studentActivityItemToDisplay =
    studentActivityItemData[studentActivityItemData?.length - 1];

  const fitnessAthleticsToDisplay =
    fitnessAthleticsData[fitnessAthleticsData?.length - 1];

  const firstFitnessImage =
    fitnessAthleticsImageData[fitnessAthleticsImageData?.length - 3]?.imagePath;
  const secondFitnessImage =
    fitnessAthleticsImageData[fitnessAthleticsImageData?.length - 2]?.imagePath;
  const thirdFitnessImage =
    fitnessAthleticsImageData[fitnessAthleticsImageData?.length - 1]?.imagePath;

  const firstCampusImage =
    campusExperienceImageData[campusExperienceImageData?.length - 4]?.imageUrl;
  const secondCampusImage =
    campusExperienceImageData[campusExperienceImageData?.length - 3]?.imageUrl;
  const thirdCampusImage =
    campusExperienceImageData[campusExperienceImageData?.length - 2]?.imageUrl;
  const fourthCampusImage =
    campusExperienceImageData[campusExperienceImageData?.length - 1]?.imageUrl;

  const id = studentLifeData[0]?.id?.toString();
  const campusId = displayStudentActivity?.id?.toString();
  const supportId = supportToDisplay?.id?.toString();
  const activityId = studentActivityToDisplay?.id?.toString();
  const fitnessId = fitnessAthleticsToDisplay?.id?.toString();

  let currentId = 1;

  const transformCampusExperienceItem = campusExperienceItemData?.map(
    (item) => {
      const newData = {
        number: `${currentId.toString().padStart(2, "0")}.`,
        content: item?.description,
      };

      currentId++;
      return newData;
    }
  );

  useEffect(() => {
    setStudentLifeId(id);
    setCampusExperienceId(campusId);
    setSupportGuidanceId(supportId);
    setStudentActivityId(activityId);
    setFitnessAthleticsId(fitnessId);
  }, [id, campusId, supportId, activityId, fitnessId]);

  const summary = [
    { name: "Overview", action: "#overview" },
    {
      name: "The Campus Experience",
      action: "#campus-experience",
    },
    {
      name: "Fitness & Athletics",
      action: "#fitness-athletics",
    },
    {
      name: "Support & Guidance",
      action: "#support-guidance",
    },
    {
      name: "Student Activities",
      action: "#student-activities",
    },
  ];

  if (isLoadingStudentLife) {
    <div className="spin">
      return <Spin size="large" />;
    </div>;
  }

  return (
    <div className={styles.container}>
      <section id="student-life" className={styles.studentLife}>
        <div>
          <div>
            <h2>STUDENT LIFE</h2>
            <div
              dangerouslySetInnerHTML={sanitizeHtml(
                studentLifeData[0]?.description
              )}
            />

            <div>
              <div>
                <Button text="Register" onClick={handleRegister} />
              </div>
            </div>
          </div>

          <div>
            {summary.map((item, index) => (
              <div className={styles.summaryItem} key={index}>
                <a href={item.action}>
                  <p>{item.name}</p>
                </a>
                {index < summary.length - 1 && <hr />}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="overview" className={styles.overview}>
        <div>
          <h3>{overviewToDisplay?.title}</h3>
          <div
            dangerouslySetInnerHTML={sanitizeHtml(
              overviewToDisplay?.description
            )}
          />
        </div>

        <MotionBox>
          <img
            width="100%"
            height={480}
            src={overviewToDisplay?.imageUrl}
            alt="overview"
          />
        </MotionBox>
      </section>

      <section className={styles.summary}>
        <div>
          {schoolSummaryData.map((item) => (
            <div>
              <h1>{item?.figure}</h1>
              <h5>{item?.title}</h5>
            </div>
          ))}
        </div>
      </section>

      <section id="campus-experience" className={styles.campusExperience}>
        <div>
          <h3>{displayStudentActivity?.title}</h3>
          <div
            dangerouslySetInnerHTML={sanitizeHtml(
              displayStudentActivity?.description
            )}
          />
        </div>

        <div>
          <div>
            {transformCampusExperienceItem.map((item) => (
              <Card>
                <div className={styles.card}>
                  <h3>{item.number}</h3>
                  <div dangerouslySetInnerHTML={sanitizeHtml(item?.content)} />
                </div>
              </Card>
            ))}
          </div>

          <div className={styles.imageGrid}>
            <div>
              <Image src={firstCampusImage} />
              <Image src={secondCampusImage} />
            </div>

            <div>
              <Image src={thirdCampusImage} />
              <Image src={fourthCampusImage} />
            </div>
          </div>
        </div>
      </section>

      <section id="fitness-athletics" className={styles.fitness}>
        <h3>{fitnessAthleticsToDisplay?.title}</h3>
        <div
          dangerouslySetInnerHTML={sanitizeHtml(
            fitnessAthleticsToDisplay?.description
          )}
        />

        <div>
          {fitnessAthleticsItemData?.map((item) => (
            <div key={item?.id}>
              <h5>{item?.title}</h5>
              <div dangerouslySetInnerHTML={sanitizeHtml(item?.description)} />
            </div>
          ))}
        </div>

        <div className={styles.athletics} ref={athleticRef}>
          <div>
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, type: "spring" }}
            >
              <motion.img
                src={firstFitnessImage}
                alt="sport1"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 100 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, type: "spring", delay: 0.2 }}
              className={styles.centeredImage}
            >
              <motion.img
                src={secondFitnessImage}
                alt="sport2"
                whileHover={{ scale: 1.1, rotate: -5 }}
                transition={{ type: "spring", stiffness: 300 }}
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, type: "spring", delay: 0.4 }}
            >
              <motion.img
                src={thirdFitnessImage}
                alt="sport3"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section id="support-guidance" className={styles.support}>
        <div>
          <h3>{supportToDisplay?.title}</h3>
          <div
            dangerouslySetInnerHTML={sanitizeHtml(
              supportToDisplay?.description
            )}
          ></div>
        </div>

        <div>
          {supportGuidanceItemData.map((item) => (
            <Accordion
              key={item.id}
              title={item.title}
              children={
                <>
                  <div
                    dangerouslySetInnerHTML={sanitizeHtml(item.description)}
                  />
                </>
              }
            />
          ))}
        </div>
      </section>

      <section id="student-activities" className={styles.activities}>
        <h3>{studentActivityToDisplay?.title}</h3>
        <div
          dangerouslySetInnerHTML={sanitizeHtml(
            studentActivityToDisplay?.description
          )}
        />

        <div>
          <div>
            <h3>{studentActivityItemToDisplay?.title}</h3>

            <div
              dangerouslySetInnerHTML={sanitizeHtml(
                studentActivityItemToDisplay?.description
              )}
            />
          </div>

          <MotionBox>
            <img
              width="100%"
              height={400}
              src={studentActivityItemToDisplay?.imageUrl}
              alt="activities"
            />
          </MotionBox>
        </div>
      </section>

      <section className={styles.eventSection}>
        <h3>News and Events</h3>
        <div>
          <span className={styles.linkLine}></span>
        </div>

        <div className={styles.posts}>
          {newsAndEventData.map((item) => (
            <MotionBox key={item.id}>
              <div className={styles.newsImg}>
                <img src={item?.imageUrl} alt="" />
              </div>
              <h4>{item.title}</h4>
              <p>{newsDates(item)}</p>
              <div dangerouslySetInnerHTML={sanitizeHtml(item.description)} />
            </MotionBox>
          ))}
        </div>
      </section>
    </div>
  );
};

export default StudentLife;
